import { useState, useEffect, useMemo, useCallback } from 'react';
import MapViewApi, { CenterData, Amenity, Supplier, FilterPayload, SupplierSearchPayload } from '.././../../Services/APIService'; // Adjust path
// import { useSnackbar } from 'notistack'; 

// Define constants that were in your Angular controller
const SUPPLIER_CODES = {
  society: 'RS',
  corporate: 'CP',
  busShelter: 'BS',
  gym: 'GY',
  saloon: 'SA',
  retailStore: 'RE',
};

const SUPPLIER_STATUS = {
  finalized: 'F',
  shortlisted: 'S',
  buffered: 'B',
  removed: 'R',
};

const INITIAL_FLAT_COUNT_SLIDER = { min: 0, max: 0, options: { floor: 0, ceil: 1000, step: 1, noSwitching: true } };
const INITIAL_FLAT_SIZE_SLIDER = { min: 0, max: 0, options: { floor: 0, ceil: 2000, step: 1, noSwitching: true } };
const INITIAL_RENTAL_PER_SQFT_SLIDER = { min: 0, max: 0, options: { floor: 0, ceil: 200, step: 1, noSwitching: true } };
const INITIAL_SALE_COST_PER_SQFT_SLIDER = { min: 0, max: 0, options: { floor: 0, ceil: 10000, step: 1, noSwitching: true } };
const INITIAL_POSSESSION_YEAR_SLIDER = { min: 1900, max: 2017, options: { floor: 1900, ceil: 2017, step: 1, noSwitching: true } };
const INITIAL_TENANTS_RATIO_SLIDER = { min: 0, max: 0, options: { floor: 0, ceil: 100, step: 1, noSwitching: true } };


export const useMapViewData = (proposalId: string) => {
//  const { enqueueSnackbar } = useSnackbar(); // For notifications
  const [loading, setLoading] = useState(false);
  const [centerData, setCenterData] = useState<CenterData[]>([]);
  const [currentCenterIndex, setCurrentCenterIndex] = useState(0);
  const [amenities, setAmenities] = useState<Amenity[]>([]);
  const [showGridView, setShowGridView] = useState(false);
  const [isRequested, setIsRequested] = useState(false); // Corresponds to ng-disabled="isRequested"

  // Filter states (simplified, would be more complex with all Angular filters)
  const [societyFilters, setSocietyFilters] = useState<any>({});
  const [corporateFilters, setCorporateFilters] = useState<any>({});
  const [realEstateAllowed, setRealEstateAllowed] = useState(false);

  // Search states for "Add New Suppliers" modal
  const [searchSupplierType, setSearchSupplierType] = useState('');
  const [searchCenterId, setSearchCenterId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Supplier[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Memoize current center for efficient access
  const currentCenter = useMemo(() => centerData[currentCenterIndex], [centerData, currentCenterIndex]);

  // --- Initial Data Fetch ---
  const fetchInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await MapViewApi.getSpaces(proposalId);
      // Assuming response.data is the array of center data
      const processedData: CenterData[] = response.data.suppliers.map((center: any) => ({
        ...center,
        suppliers_allowed: {
          society_show: !!center.suppliers[SUPPLIER_CODES.society],
          corporate_show: !!center.suppliers[SUPPLIER_CODES.corporate],
          busShelter_allowed: !!center.suppliers[SUPPLIER_CODES.busShelter],
          gym_allowed: !!center.suppliers[SUPPLIER_CODES.gym],
          saloon_allowed: !!center.suppliers[SUPPLIER_CODES.saloon],
          retailStore_allowed: !!center.suppliers[SUPPLIER_CODES.retailStore],
          // ... add other supplier types
        },
        filters_meta: {}, // Initialize as empty
        inventory_meta: {}, // Initialize as empty
        summary_meta: {}, // Initialize as empty
        RS_filters: { // Initialize RS filters similar to Angular's scope initialization
          inventory: [
            { name: 'Poster(PO)', code: 'PO', selected: false },
            { name: 'Standee(ST)', code: 'ST', selected: false },
            { name: 'Stall(SL)', code: 'SL', selected: false },
            { name: 'Flyer(FL)', code: 'FL', selected: false },
            { name: 'Car(CD)', code: 'CD', selected: false },
            { name: 'PO & FL', code: 'POFL', selected: false },
            { name: 'ST & FL', code: 'STFL', selected: false },
            { name: 'SL & FL', code: 'SLFL', selected: false },
            { name: 'CD & FL', code: 'CDFL', selected: false },
            { name: 'PO & SL & FL', code: 'POSLFL', selected: false },
            { name: 'ST & SL& FL', code: 'STSLFL', selected: false },
            { name: 'PO & CD & FL', code: 'POCDFL', selected: false },
            { name: 'ST & CD & FL', code: 'STCDFL', selected: false },
          ],
          locality_rating: [
            { name: 'Ultra High', code: 'UH', selected: false },
            { name: 'High', code: 'HH', selected: false },
            { name: 'Medium High', code: 'MH', selected: false },
            { name: 'Standard', code: 'ST', selected: false },
          ],
          quality_type: [
            { name: 'Ultra High', code: 'UH', selected: false },
            { name: 'High', code: 'HH', selected: false },
            { name: 'Medium High', code: 'MH', selected: false },
            { name: 'Standard', code: 'ST', selected: false },
          ],
          quantity_type: [
            { name: 'Small', code: 'SM', selected: false },
            { name: 'Medium', code: 'MD', selected: false },
            { name: 'Large', code: 'LA', selected: false },
            { name: 'Very Large', code: 'VL', selected: false },
          ],
          flat_type: [
            { name: '1 RK', code: '1R', selected: false, flat_count: INITIAL_FLAT_COUNT_SLIDER, flat_size: INITIAL_FLAT_SIZE_SLIDER },
            { name: '1 BHK', code: '1B', selected: false, flat_count: INITIAL_FLAT_COUNT_SLIDER, flat_size: INITIAL_FLAT_SIZE_SLIDER },
            { name: '1.5 BHK', code: '1-5B', selected: false, flat_count: INITIAL_FLAT_COUNT_SLIDER, flat_size: INITIAL_FLAT_SIZE_SLIDER },
            { name: '2 BHK', code: '2B', selected: false, flat_count: INITIAL_FLAT_COUNT_SLIDER, flat_size: INITIAL_FLAT_SIZE_SLIDER },
            { name: '2.5 BHK', code: '2-5B', selected: false, flat_count: INITIAL_FLAT_COUNT_SLIDER, flat_size: INITIAL_FLAT_SIZE_SLIDER },
            { name: '3 BHK', code: '3B', selected: false, flat_count: INITIAL_FLAT_COUNT_SLIDER, flat_size: INITIAL_FLAT_SIZE_SLIDER },
            { name: '4 BHK', code: '4B', selected: false, flat_count: INITIAL_FLAT_COUNT_SLIDER, flat_size: INITIAL_FLAT_SIZE_SLIDER },
            { name: '5 BHK', code: '5B', selected: false, flat_count: INITIAL_FLAT_COUNT_SLIDER, flat_size: INITIAL_FLAT_SIZE_SLIDER },
            { name: 'PENT HOUSE', code: 'PH', selected: false, flat_count: INITIAL_FLAT_COUNT_SLIDER, flat_size: INITIAL_FLAT_SIZE_SLIDER },
            { name: 'DUPLEX', code: 'DP', selected: false, flat_count: INITIAL_FLAT_COUNT_SLIDER, flat_size: INITIAL_FLAT_SIZE_SLIDER },
            { name: 'ROW HOUSE', code: 'RH', selected: false, flat_count: INITIAL_FLAT_COUNT_SLIDER, flat_size: INITIAL_FLAT_SIZE_SLIDER },
          ],
          amenities: [], // This will be populated from a separate API call
          flat_avg_rental_persqft: INITIAL_RENTAL_PER_SQFT_SLIDER,
          flat_sale_cost_persqft: INITIAL_SALE_COST_PER_SQFT_SLIDER,
          ratio_of_tenants_to_flats: INITIAL_TENANTS_RATIO_SLIDER,
          possession_year: INITIAL_POSSESSION_YEAR_SLIDER,
          is_standalone_society: { selected: false },
        },
        CP_filters: { // Initialize CP filters
          inventory: [
            { name: 'Poster(PO)', code: 'PO', selected: false },
            { name: 'Standee(ST)', code: 'ST', selected: false },
            { name: 'Stall(SL)', code: 'SL', selected: false },
            { name: 'Flyer(FL)', code: 'FL', selected: false },
            { name: 'Car(CD)', code: 'CD', selected: false },
            { name: 'PO & FL', code: 'POFL', selected: false },
            { name: 'ST & FL', code: 'STFL', selected: false },
            { name: 'SL & FL', code: 'SLFL', selected: false },
            { name: 'CD & FL', code: 'CDFL', selected: false },
            { name: 'PO & SL & FL', code: 'POSLFL', selected: false },
            { name: 'ST & SL& FL', code: 'STSLFL', selected: false },
            { name: 'PO & CD & FL', code: 'POCDFL', selected: false },
            { name: 'ST & CD & FL', code: 'STCDFL', selected: false },
          ],
          locality_rating: [
            { name: 'Ultra High', code: 'UH', selected: false },
            { name: 'High', code: 'HH', selected: false },
            { name: 'Medium High', code: 'MH', selected: false },
            { name: 'Standard', code: 'ST', selected: false },
          ],
          quality_type: [
            { name: 'Ultra High', code: 'UH', selected: false },
            { name: 'High', code: 'HH', selected: false },
            { name: 'Medium High', code: 'MH', selected: false },
            { name: 'Standard', code: 'ST', selected: false },
          ],
          quantity_type: [
            { name: 'Small', code: 'SM', selected: false },
            { name: 'Medium', code: 'MD', selected: false },
            { name: 'Large', code: 'LA', selected: false },
            { name: 'Very Large', code: 'VL', selected: false },
          ],
          employee_count: [
            { name: '0-1000', code: { min: '0', max: '1000' }, selected: false },
            { name: '1000-3000', code: { min: '1000', max: '3000' }, selected: false },
            { name: '3000-6000', code: { min: '3000', max: '6000' }, selected: false },
            { name: '6000-10000', code: { min: '6000', max: '10000' }, selected: false },
          ],
        }
      }));

      setCenterData(processedData);
      setSocietyFilters(processedData[currentCenterIndex]?.RS_filters || {});
      setCorporateFilters(processedData[currentCenterIndex]?.CP_filters || {});

    } catch (error) {
    //  enqueueSnackbar('Failed to fetch initial map data.', { variant: 'error' });
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [proposalId, currentCenterIndex]);

  const fetchAmenities = useCallback(async () => {
    try {
      const data = await MapViewApi.getAmenityFilters();
      setAmenities(data);
      // Update RS_filters in all centers with fetched amenities
      setCenterData(prevData =>
        prevData.map(center => ({
          ...center,
          RS_filters: {
            ...center.RS_filters,
            amenities: data.map((amenity: Amenity) => ({ ...amenity, selected: false }))
          }
        }))
      );
    } catch (error) {
  //    enqueueSnackbar('Failed to fetch amenity filters.', { variant: 'error' });
      console.error(error);
    }
  }, []);

  useEffect(() => {
    fetchInitialData();
    fetchAmenities(); // Fetch amenities once
  }, [fetchInitialData, fetchAmenities]);

  // --- Handlers for UI Interactions ---

  const handleChangeCenterDropdown = useCallback((centerId: string) => {
    const index = centerData.findIndex(c => c.center.id === centerId);
    if (index !== -1) {
      setCurrentCenterIndex(index);
      setSocietyFilters(centerData[index]?.RS_filters || {});
      setCorporateFilters(centerData[index]?.CP_filters || {});
    }
  }, [centerData]);

  const handleApplyFilters = useCallback(async (supplierCode: string, filters: any) => {
    setLoading(true);
    try {
      const currentCenterDetails = centerData[currentCenterIndex];
      const payload: FilterPayload = {
        supplier_type_code: supplierCode,
        proposal_id: proposalId,
        center_id: currentCenterDetails.center.id,
        common_filters: {
          latitude: currentCenterDetails.center.latitude,
          longitude: currentCenterDetails.center.longitude,
          radius: currentCenterDetails.center.radius,
          quality: filters.quality_type?.filter((f: any) => f.selected).map((f: any) => f.code) || [],
          locality: filters.locality_rating?.filter((f: any) => f.selected).map((f: any) => f.code) || [],
          quantity: filters.quantity_type?.filter((f: any) => f.selected).map((f: any) => f.code) || [],
        },
        inventory_filters: filters.inventory?.filter((f: any) => f.selected).map((f: any) => f.code) || [],
        priority_index_filters: {
          amenities: filters.amenities?.filter((a: any) => a.selected).map((a: any) => a.code) || [],
          flat_type: {}, // Needs more complex mapping from selected flat types and their slider values
          flat_avg_rental_persqft: filters.flat_avg_rental_persqft,
          flat_sale_cost_persqft: filters.flat_sale_cost_persqft,
          ratio_of_tenants_to_flats: filters.ratio_of_tenants_to_flats ? {
            min: filters.ratio_of_tenants_to_flats.min / 100,
            max: filters.ratio_of_tenants_to_flats.max / 100
          } : undefined,
          possession_year: filters.possession_year,
          is_standalone_society: filters.is_standalone_society?.selected,
          employees_count: filters.employee_count?.filter((f: any) => f.selected).map((f: any) => f.code) || [],
          real_estate_allowed: realEstateAllowed,
        },
      };

      // Handle flat_type specific filtering
      if (filters.flat_type) {
        filters.flat_type.forEach((ft: any) => {
          if (ft.selected) {
            (payload.priority_index_filters.flat_type as any)[ft.code] = {
              count: ft.flat_count.max !== ft.flat_count.options.floor ? ft.flat_count : undefined,
              size: ft.flat_size.max !== ft.flat_size.options.floor ? ft.flat_size : undefined,
            };
          }
        });
      }

      const response = await MapViewApi.getFilterSuppliers(payload);
      // Update the specific center's suppliers and meta based on response
      setCenterData(prevData => prevData.map((center, idx) => {
        if (idx === currentCenterIndex) {
          return {
            ...center,
            suppliers: {
              ...center.suppliers,
              [supplierCode]: response.data.suppliers[supplierCode],
            },
            suppliers_meta: {
              ...center.suppliers_meta,
              [supplierCode]: response.data.suppliers_meta[supplierCode],
            },
            // Recalculate summary meta for the updated supplierCode
            summary_meta: {
                ...center.summary_meta,
                [supplierCode]: calculateSummary(response.data.suppliers[supplierCode]),
            }
          };
        }
        return center;
      }));
    //  enqueueSnackbar('Filters applied successfully!', { variant: 'success' });
    } catch (error) {
    //  enqueueSnackbar('Failed to apply filters.', { variant: 'error' });
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [centerData, currentCenterIndex, proposalId, realEstateAllowed]);

  const handleUpdateSupplierStatus = useCallback(async (supplier: Supplier, newStatus: 'F' | 'S' | 'B' | 'R') => {
    setLoading(true);
    try {
      const data = {
        center_id: currentCenter?.center.id || '',
        supplier_id: supplier.supplier_id,
        status: newStatus,
        supplier_type_code: supplier.supplier_id.substring(0,2) // Assuming supplier_id prefix indicates type (e.g., 'RS' for Residential Society)
      };
      await MapViewApi.updateSupplierStatus(proposalId, data);

      // Optimistically update UI
      setCenterData(prevData => prevData.map((center, cIdx) => {
        if (cIdx === currentCenterIndex) {
          const updatedSuppliers = { ...center.suppliers };
          const supplierTypeCode = data.supplier_type_code; // Get the specific supplier type code
          if (updatedSuppliers[supplierTypeCode]) {
            updatedSuppliers[supplierTypeCode] = updatedSuppliers[supplierTypeCode].map(s =>
              s.supplier_id === supplier.supplier_id ? { ...s, status: newStatus } : s
            );
          }
           // Recalculate summary meta for the affected supplier type
          const newSummaryMeta = {
            ...center.summary_meta,
            [supplierTypeCode]: calculateSummary(updatedSuppliers[supplierTypeCode])
          };
          return { ...center, suppliers: updatedSuppliers, summary_meta: newSummaryMeta };
        }
        return center;
      }));
   //   enqueueSnackbar('Supplier status updated successfully!', { variant: 'success' });
    } catch (error) {
  //    enqueueSnackbar('Failed to update supplier status.', { variant: 'error' });
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [currentCenter, proposalId, currentCenterIndex, centerData]);


  const handleSearchSuppliers = useCallback(async (params: SupplierSearchPayload) => {
    setLoading(true);
    setSearchError(null);
    try {
      const response = await MapViewApi.searchSuppliers(params);
      if (response.data && response.data.length > 0) {
        setSearchResults(response.data);
      } else {
        setSearchResults([]);
        setSearchError("No results found.");
      }
    } catch (error) {
      setSearchResults([]);
      setSearchError("Error searching suppliers.");
    //  enqueueSnackbar('Failed to search suppliers.', { variant: 'error' });
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleAddSupplierToCenter = useCallback(async (supplier: Supplier) => {
    setLoading(true);
    try {
      // In Angular, it was pushing to local data and then updating status.
      // Here, we should update the supplier's status to 'S' (Shortlisted)
      // and then save the proposal data to persist the change.
      const updatedSupplier = { ...supplier, status: SUPPLIER_STATUS.shortlisted };

      const currentCenterCopy = JSON.parse(JSON.stringify(currentCenter)); // Deep copy
      const supplierTypeCode = updatedSupplier.supplier_id.substring(0,2); // infer type from ID

      if (!currentCenterCopy.suppliers[supplierTypeCode]) {
        currentCenterCopy.suppliers[supplierTypeCode] = [];
      }
      currentCenterCopy.suppliers[supplierTypeCode].push(updatedSupplier);

      // Now, save the updated `currentCenterCopy` which includes the new supplier
      // Note: The Angular code calls updateSupplierStatus, which uses create-final-proposal API.
      // This implies that this API is used for both initial save and subsequent status updates/additions.
      // It's crucial to send the entire `center_data` array to `saveData` as your backend expects it.

      const updatedAllCenters = centerData.map((c, idx) =>
        idx === currentCenterIndex ? currentCenterCopy : c
      );

      await MapViewApi.saveData(proposalId, updatedAllCenters);

      setCenterData(updatedAllCenters); // Update local state with the change
    //  enqueueSnackbar('Supplier added successfully!', { variant: 'success' });
    } catch (error) {
   //   enqueueSnackbar('Failed to add supplier.', { variant: 'error' });
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [currentCenter, currentCenterIndex, proposalId, centerData]);


  const handleSaveData = useCallback(async () => {
    setLoading(true);
    try {
      // Logic for saving selected filters needs to be implemented here
      // For simplicity, just sending current centerData
      await MapViewApi.saveData(proposalId, centerData);
    //  enqueueSnackbar('Data saved successfully!', { variant: 'success' });
    } catch (error) {
  //    enqueueSnackbar('Failed to save data.', { variant: 'error' });
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [proposalId, centerData]);

  const toggleGridView = useCallback(() => {
    setShowGridView(prev => !prev);
  }, []);

  // --- Helper Functions (Reimplementing Angular's helper logic) ---

  const calculateSummary = useCallback((suppliers: Supplier[]) => {
    const summary = {
      count: suppliers.length,
      flat_count: 0,
      tower_count: 0,
      unit_primary_count: 0,
      unit_secondary_count: 0,
      finalized: { count: 0, flat_count: 0, tower_count: 0, poster_count: 0, flier_count: 0, standee_count: 0, stall_count: 0, impressions: { poster: 0, standee: 0, stall: 0, flier: 0 } },
      shortlisted: { count: 0, flat_count: 0, tower_count: 0, poster_count: 0, flier_count: 0, standee_count: 0, stall_count: 0, impressions: { poster: 0, standee: 0, stall: 0, flier: 0 } },
      buffered: { count: 0 },
      removed: { count: 0 },
    };

    suppliers.forEach(supplier => {
      // Calculate overall counts
      if (supplier.flat_count !== undefined) summary.flat_count += supplier.flat_count;
      if (supplier.tower_count !== undefined) summary.tower_count += supplier.tower_count;
      if (supplier.unit_primary_count !== undefined) summary.unit_primary_count += supplier.unit_primary_count;
      if (supplier.unit_secondary_count !== undefined) summary.unit_secondary_count += supplier.unit_secondary_count;

      // Calculate status-specific counts
      if (supplier.status === SUPPLIER_STATUS.finalized) {
        summary.finalized.count += 1;
        if (supplier.flat_count !== undefined) summary.finalized.flat_count += supplier.flat_count;
        if (supplier.tower_count !== undefined) summary.finalized.tower_count += supplier.tower_count;
        // Add specific inventory counts if applicable (needs mapping from supplier.inventory_types or similar)
        // For example: summary.finalized.poster_count += supplier.poster_count || 0;
      } else if (supplier.status === SUPPLIER_STATUS.shortlisted) {
        summary.shortlisted.count += 1;
        if (supplier.flat_count !== undefined) summary.shortlisted.flat_count += supplier.flat_count;
        if (supplier.tower_count !== undefined) summary.shortlisted.tower_count += supplier.tower_count;
      } else if (supplier.status === SUPPLIER_STATUS.buffered) {
        summary.buffered.count += 1;
      } else if (supplier.status === SUPPLIER_STATUS.removed) {
        summary.removed.count += 1;
      }
    });

    // Calculate impressions (simplified, based on your Angular logic)
    // This part is highly dependent on how inventory types and their counts are stored in `Supplier` interface.
    // For now, it's a direct translation of the flat_count based logic.
    summary.finalized.impressions.poster = summary.finalized.flat_count * 4 * 7 * 2;
    summary.finalized.impressions.flier = summary.finalized.flat_count * 4 * 1;
    summary.finalized.impressions.standee = summary.finalized.flat_count * 4 * 7 * 2; // Assuming always calculated if relevant
    summary.finalized.impressions.stall = summary.finalized.flat_count * 4 * 2; // Assuming always calculated if relevant

    summary.shortlisted.impressions.poster = summary.shortlisted.flat_count * 4 * 7 * 2;
    summary.shortlisted.impressions.flier = summary.shortlisted.flat_count * 4 * 1;
    summary.shortlisted.impressions.standee = summary.shortlisted.flat_count * 4 * 7 * 2;
    summary.shortlisted.impressions.stall = summary.shortlisted.flat_count * 4 * 2;

    return summary;
  }, []);


  const getComprehensiveSummary = useCallback(() => {
    const comprehensiveSummary: { [key: string]: any } = {};

    Object.values(SUPPLIER_CODES).forEach(code => {
      comprehensiveSummary[code] = {
        count: 0,
        flat_count: 0,
        tower_count: 0,
        unit_primary_count: 0,
        unit_secondary_count: 0,
        finalized: { count: 0, flat_count: 0, tower_count: 0, poster_count: 0, flier_count: 0, standee_count: 0, stall_count: 0, impressions: { poster: 0, standee: 0, stall: 0, flier: 0 } },
        shortlisted: { count: 0, flat_count: 0, tower_count: 0, poster_count: 0, flier_count: 0, standee_count: 0, stall_count: 0, impressions: { poster: 0, standee: 0, stall: 0, flier: 0 } },
        buffered: { count: 0 },
        removed: { count: 0 },
      };

      centerData.forEach(center => {
        if (center.summary_meta[code]) {
          const s = center.summary_meta[code];
          comprehensiveSummary[code].count += s.count;
          comprehensiveSummary[code].flat_count += s.flat_count || 0;
          comprehensiveSummary[code].tower_count += s.tower_count || 0;
          comprehensiveSummary[code].unit_primary_count += s.unit_primary_count || 0;
          comprehensiveSummary[code].unit_secondary_count += s.unit_secondary_count || 0;

          comprehensiveSummary[code].finalized.count += s.finalized.count;
          comprehensiveSummary[code].finalized.flat_count += s.finalized.flat_count;
          comprehensiveSummary[code].finalized.tower_count += s.finalized.tower_count;
          comprehensiveSummary[code].finalized.poster_count += s.finalized.poster_count;
          comprehensiveSummary[code].finalized.flier_count += s.finalized.flier_count;
          comprehensiveSummary[code].finalized.standee_count += s.finalized.standee_count;
          comprehensiveSummary[code].finalized.stall_count += s.finalized.stall_count;

          comprehensiveSummary[code].shortlisted.count += s.shortlisted.count;
          comprehensiveSummary[code].shortlisted.flat_count += s.shortlisted.flat_count;
          comprehensiveSummary[code].shortlisted.tower_count += s.shortlisted.tower_count;
          comprehensiveSummary[code].shortlisted.poster_count += s.shortlisted.poster_count;
          comprehensiveSummary[code].shortlisted.flier_count += s.shortlisted.flier_count;
          comprehensiveSummary[code].shortlisted.standee_count += s.shortlisted.standee_count;
          comprehensiveSummary[code].shortlisted.stall_count += s.shortlisted.stall_count;

          comprehensiveSummary[code].buffered.count += s.buffered.count;
          comprehensiveSummary[code].removed.count += s.removed.count;
        }
      });
      // Recalculate impressions for comprehensive summary
      comprehensiveSummary[code].finalized.impressions.poster = comprehensiveSummary[code].finalized.flat_count * 4 * 7 * 2;
      comprehensiveSummary[code].finalized.impressions.flier = comprehensiveSummary[code].finalized.flat_count * 4 * 1;
      comprehensiveSummary[code].finalized.impressions.standee = comprehensiveSummary[code].finalized.flat_count * 4 * 7 * 2;
      comprehensiveSummary[code].finalized.impressions.stall = comprehensiveSummary[code].finalized.flat_count * 4 * 2;

      comprehensiveSummary[code].shortlisted.impressions.poster = comprehensiveSummary[code].shortlisted.flat_count * 4 * 7 * 2;
      comprehensiveSummary[code].shortlisted.impressions.flier = comprehensiveSummary[code].shortlisted.flat_count * 4 * 1;
      comprehensiveSummary[code].shortlisted.impressions.standee = comprehensiveSummary[code].shortlisted.flat_count * 4 * 7 * 2;
      comprehensiveSummary[code].shortlisted.impressions.stall = comprehensiveSummary[code].shortlisted.flat_count * 4 * 2;
    });

    return comprehensiveSummary;
  }, [centerData]);

  const comprehensiveSummary = useMemo(() => getComprehensiveSummary(), [getComprehensiveSummary]);


  return {
    loading,
    centerData,
    currentCenter,
    currentCenterIndex,
    setCurrentCenterIndex,
    amenities,
    showGridView,
    toggleGridView,
    isRequested, // You'd set this based on proposal state/permissions
    societyFilters,
    setSocietyFilters,
    corporateFilters,
    setCorporateFilters,
    realEstateAllowed,
    setRealEstateAllowed,
    handleApplyFilters,
    handleUpdateSupplierStatus,
    handleSaveData,
    handleChangeCenterDropdown,
    handleSearchSuppliers,
    searchResults,
    setSearchResults,
    searchSupplierType,
    setSearchSupplierType,
    searchCenterId,
    setSearchCenterId,
    searchQuery,
    setSearchQuery,
    searchError,
    handleAddSupplierToCenter,
    SUPPLIER_CODES,
    SUPPLIER_STATUS,
    comprehensiveSummary,
    // You would expose other calculated values like total_societies_count, etc. as needed
  };
};